import { useState, useEffect, useCallback, useRef } from 'react';

export const useProductSelection = (addToCart) => {
  const [selectionModal, setSelectionModal] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedSupplements, setSelectedSupplements] = useState([]);
  // Guard against the auto-confirm useEffect re-firing while the modal is
  // being closed. Without this, a product whose selection is already
  // "complete" on open (e.g. all groups optional or a legacy product with a
  // required=0 group that resolves as complete on mount) can enter a
  // confirm→re-render→confirm loop that freezes the whole POS.
  const submittingRef = useRef(false);

  const productNeedsSelection = (product) => {
    return product.requires_selection ||
      product.requires_bread_selection ||
      (product.options && product.options.length > 0) ||
      (product.supplements && product.supplements.length > 0);
  };

  const getOptionGroups = (product) => {
    const groups = {};
    (product.options || []).forEach(opt => {
      const group = opt.group || 'opciones';
      if (!groups[group]) groups[group] = [];
      groups[group].push(opt);
    });
    return groups;
  };

  const getGroupLabel = (group) => {
    const labels = {
      'tipo_pan': 'Tipo de Pan', 'ensalada': 'Ensalada', 'principal': 'Plato Principal',
      'plato_principal': 'Plato Principal', 'postre': 'Postre', 'tamano': 'Tamaño',
      'temperatura': 'Temperatura', 'opciones': 'Opciones'
    };
    return labels[group] || group.charAt(0).toUpperCase() + group.slice(1).replace(/_/g, ' ');
  };

  const getRequiredCount = (product, group) => {
    if (product.requires_bread_selection && group === 'tipo_pan') return 1;
    if (product.selection_requirements) return product.selection_requirements[group] || 0;
    if (product.options?.some(o => o.group === group)) return 1;
    return 0;
  };

  const handleSelectOption = (group, option) => {
    const product = selectionModal;
    const required = getRequiredCount(product, group);
    setSelectedOptions(prev => {
      const current = prev[group] || [];
      const optName = option.name?.es || option.name || '';
      const isSelected = current.some(o => (o.name?.es || o.name) === optName);
      if (isSelected) return { ...prev, [group]: current.filter(o => (o.name?.es || o.name) !== optName) };
      if (required === 1) return { ...prev, [group]: [option] };
      if (current.length >= required && required > 0) return { ...prev, [group]: [...current.slice(1), option] };
      return { ...prev, [group]: [...current, option] };
    });
  };

  const handleToggleSupplement = (supplement) => {
    const supName = supplement.name?.es || supplement.name || '';
    setSelectedSupplements(prev => {
      const isSelected = prev.some(s => (s.name?.es || s.name) === supName);
      if (isSelected) return prev.filter(s => (s.name?.es || s.name) !== supName);
      return [...prev, supplement];
    });
  };

  const isSelectionComplete = useCallback(() => {
    if (!selectionModal) return false;
    const optionGroups = getOptionGroups(selectionModal);
    for (const group of Object.keys(optionGroups)) {
      const required = getRequiredCount(selectionModal, group);
      const selected = (selectedOptions[group] || []).length;
      if (required > 0 && selected < required) return false;
    }
    return true;
  }, [selectionModal, selectedOptions]);

  const handleConfirmSelection = useCallback(() => {
    if (!selectionModal || submittingRef.current) return;
    submittingRef.current = true;
    try {
      const product = selectionModal;
      const allSelected = [];
      let extraCost = 0;
      Object.values(selectedOptions).flat().forEach(opt => {
        allSelected.push(opt.name?.es || opt.name || '');
        extraCost += Number(opt.price_modifier) || 0;
      });
      selectedSupplements.forEach(sup => {
        allSelected.push(`+${sup.name?.es || sup.name || ''}`);
        extraCost += Number(sup.price_modifier) || 0;
      });
      const getProductName = (p) => {
        if (!p?.name) return 'Producto';
        if (typeof p.name === 'string') return p.name;
        return p.name.es || p.name.en || p.name.cat || 'Producto';
      };
      const productWithOptions = {
        ...product,
        name: getProductName(product),
        selectedOptions: allSelected,
        optionsText: allSelected.join(', '),
        price: (Number(product.price) || 0) + extraCost,
        originalPrice: Number(product.price) || 0,
        extraCost,
        cartId: `${product.id}-${Date.now()}`
      };
      addToCart(productWithOptions);
      setSelectionModal(null);
      setSelectedOptions({});
      setSelectedSupplements([]);
    } catch (e) {
      // Never let a bad product freeze the whole panel.
      console.error('[useProductSelection] handleConfirmSelection error', e);
      setSelectionModal(null);
    } finally {
      // Release the flag on the next tick so the useEffect stops firing.
      setTimeout(() => { submittingRef.current = false; }, 0);
    }
  }, [selectionModal, selectedOptions, selectedSupplements, addToCart]);

  // Auto-confirm when all required options are selected. The
  // `submittingRef` guard + the "empty selection" early-return together
  // prevent the freeze that happened when a product's group was already
  // considered complete on mount (e.g. Cortado in prod).
  useEffect(() => {
    if (!selectionModal || submittingRef.current) return;
    if (Object.keys(selectedOptions).length === 0) return;
    if (isSelectionComplete()) {
      const timer = setTimeout(() => handleConfirmSelection(), 250);
      return () => clearTimeout(timer);
    }
  }, [selectedOptions, selectionModal, isSelectionComplete, handleConfirmSelection]);

  const openSelection = (product) => {
    if (!product) return;
    if (productNeedsSelection(product)) {
      submittingRef.current = false;
      setSelectionModal(product);
      setSelectedOptions({});
      setSelectedSupplements([]);
    } else {
      addToCart(product);
    }
  };

  return {
    selectionModal, setSelectionModal,
    selectedOptions, selectedSupplements,
    productNeedsSelection, getOptionGroups, getGroupLabel, getRequiredCount,
    handleSelectOption, handleToggleSupplement,
    isSelectionComplete, handleConfirmSelection,
    openSelection
  };
};
